"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Dumbbell, Sun, Utensils, Calendar, ChevronDown, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const planData = [{
  day: 1,
  title: "Przylot i Aklimatyzacja",
  isMajor: true,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/camp_arrival_color.png",
  imageClassName: "bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/camp_arrival_color.png)]",
  time1: "14:00",
  act1: "Zakwaterowanie w resorcie",
  icon1: "sun",
  time2: "18:00",
  act2: "Kolacja powitalna i omówienie planu",
  icon2: "plate",
  desc: "Witamy na Koh Samui! Pierwszy dzień to czas na odpoczynek po podróży, zakwaterowanie w komfortowych warunkach i zapoznanie się z całą ekipą oraz trenerami, w tym z Dawidem Kasperskim. Wieczorem spotkamy się na wspólnej kolacji, aby omówić szczegóły naszego 12-dniowego planu bojowego."
}, {
  day: 2,
  title: "Pierwsze Starcie",
  isMajor: false,
  image: "https://newwavecamp.com/wp-content/uploads/2026/03/0c6c1a8c-3e6a-47e6-919d-a8d881b7729d-e1774317868224.jpg",
  imageClassName: "bg-cover bg-center bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/0c6c1a8c-3e6a-47e6-919d-a8d881b7729d-e1774317868224.jpg)]",
  time1: "11:00",
  act1: "Muay Thai - Podstawy i Kondycja",
  icon1: "gloves",
  time2: "16:00",
  act2: "Kickboxing - Technika",
  icon2: "gloves",
  desc: "Wchodzimy w rytm treningowy. Dwa mocne bloki pod okiem lokalnych trenerów Lamai Muay Thai oraz Dawida. Skupimy się na przypomnieniu podstaw, prawidłowej postawie i budowaniu bazy tlenowej niezbędnej na kolejne dni."
}, {
  day: 3,
  title: "Budowanie Wytrzymałości",
  isMajor: false,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/pad_work_intensity.png",
  imageClassName: "bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/pad_work_intensity.png)]",
  time1: "10:30",
  act1: "Muay Thai - Praca na tarczach",
  icon1: "gloves",
  time2: "16:00",
  act2: "Kickboxing - Sparingi zadaniowe",
  icon2: "gloves",
  desc: "Zwiększamy intensywnost. Poranna sesja to mordercza praca na tarczach z tajskimi trenerami, która wyciągnie z Was siódme poty. Po południu pierwsze zadaniowe sparingi, gdzie skupimy się na kontroli dystansu i timingu."
}, {
  day: 4,
  title: "Wodospady Na Muang",
  isMajor: true,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/namuang_waterfall.png",
  imageClassName: "bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/namuang_waterfall.png)]",
  time1: "09:30",
  act1: "Trekking i kąpiel w wodospadach",
  icon1: "sun",
  time2: "14:00",
  act2: "Degustacja lokalnego street foodu",
  icon2: "plate",
  desc: "Czas na zasłużoną regenerację i eksplorację wyspy. Udamy się na malownicze wodospady Na Muang, gdzie będzie można popływać w naturalnych basenach. Po południu zanurzymy się w smakach prawdziwej, tajskiej kuchni na lokalnym targu."
}, {
  day: 5,
  title: "Powrót na Matę",
  isMajor: false,
  image: "https://newwavecamp.com/wp-content/uploads/2026/03/IMG_7388-e1774317839741.jpg",
  imageClassName: "bg-cover bg-center bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/IMG_7388-e1774317839741.jpg)]",
  time1: "10:00",
  act1: "Muay Thai - Tajniki Klinczu",
  icon1: "gloves",
  time2: "16:00",
  act2: "Kickboxing - Zaawansowane kombinacje",
  icon2: "gloves",
  desc: "Wracamy do ciężkiej pracy z nową energią. Rano skupiamy się na specyfice tajskiego klinczu – technice, która często decyduje o wygranej. Po południu szlifujemy długie, bokserskie i kickboxerskie kombinacje uderzeń."
}, {
  day: 6,
  title: "Siła i Szybkość",
  isMajor: false,
  image: "https://newwavecamp.com/wp-content/uploads/2026/03/013c7cad-1a6d-4dc3-a50d-03ad15f3d854-e1774317501542.jpg",
  imageClassName: "bg-cover bg-center bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/013c7cad-1a6d-4dc3-a50d-03ad15f3d854-e1774317501542.jpg)]",
  time1: "10:30",
  act1: "Muay Thai - Sparingi",
  icon1: "gloves",
  time2: "16:00",
  act2: "Trening motoryczny i siłowy",
  icon2: "gloves",
  desc: "Dzień pełen wyzwań. Poranne sparingi pozwolą sprawdzić nabyte umiejętności w praktyce. Popołudnie poświęcimy na budowanie siły eksplozywnej i szybkości, korzystając ze sprzętu dostępnego w Lamai Boxing."
}, {
  day: 7,
  title: "Aktywna Regeneracja",
  isMajor: false,
  image: "https://newwavecamp.com/wp-content/uploads/2026/03/468618651_18320973913095949_8577082515011612481_n-e1774317557276.jpg",
  imageClassName: "bg-cover bg-center bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/468618651_18320973913095949_8577082515011612481_n-e1774317557276.jpg)]",
  time1: "10:00",
  act1: "Joga i głębokie rozciąganie",
  icon1: "sun",
  time2: "15:00",
  act2: "Czas wolny na plaży Lamai",
  icon2: "sun",
  desc: "Półmetek obozu. Dajemy ciałom odpocząć i zregenerować mikrourazy. Poranna sesja jogi pomoże przywrócić elastyczność mięśniom. Reszta dnia to czas wolny na relaks na pięknej plaży Lamai, masaże lub zakupy."
}, {
  day: 8,
  title: "Rejs na Koh Tan",
  isMajor: true,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/kohtan_boat.png",
  imageClassName: "bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/kohtan_boat.png)]",
  time1: "09:00",
  act1: "Rejs łodzią i snorkeling",
  icon1: "sun",
  time2: "18:00",
  act2: "Kolacja z owocami morza na plaży",
  icon2: "plate",
  desc: "Całodniowa wycieczka na pobliską, dziką wyspę Koh Tan. Będziemy nurkować z rurką w krystalicznie czystej wodzie, podziwiać rafy koralowe i relaksować się z dala od cywilizacji. Dzień zakończymy wyśmienitą kolacją ze świeżych owoców morza."
}, {
  day: 9,
  title: "Ostatnia Prosta",
  isMajor: false,
  image: "https://newwavecamp.com/wp-content/uploads/2026/03/6f10d2a3-abd4-4aee-b2f4-923c0653b886-e1774317390298.jpg",
  imageClassName: "bg-cover bg-center bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/6f10d2a3-abd4-4aee-b2f4-923c0653b886-e1774317390298.jpg)]",
  time1: "10:30",
  act1: "Muay Thai - Łokcie i kolana",
  icon1: "gloves",
  time2: "16:00",
  act2: "Kickboxing - Taktyka walki",
  icon2: "gloves",
  desc: "Wracamy do treningów ze zdwojoną siłą. Skupiamy się na najbardziej destrukcyjnych technikach Muay Thai – ciosach łokciami i kolanami. Popołudniowa sesja to praca nad taktyką, ringowym IQ i czytaniem przeciwnika."
}, {
  day: 10,
  title: "Mocne Sparingi",
  isMajor: false,
  image: "https://newwavecamp.com/wp-content/uploads/2026/03/2e86b606-1907-4fea-a640-c708b0cab8cd-e1774317595222.jpg",
  imageClassName: "bg-cover bg-center bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/2e86b606-1907-4fea-a640-c708b0cab8cd-e1774317595222.jpg)]",
  time1: "10:00",
  act1: "Muay Thai - Pełne sparingi",
  icon1: "gloves",
  time2: "16:00",
  act2: "Praca na workach i kondycja",
  icon2: "gloves",
  desc: "Najtrudniejszy dzień treningowy całego wyjazdu. Pełne sparingi z lokalnymi zawodnikami i trenerami. To prawdziwy sprawdzian charakteru, woli walki i nabytych umiejętności. Po południu dobijamy kondycję na workach."
}, {
  day: 11,
  title: "Szlifowanie Formy",
  isMajor: false,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/image_g04icq4m.jpg",
  imageClassName: "bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/image_g04icq4m.jpg)]",
  time1: "10:30",
  act1: "Muay Thai - Lekki trening techniczny",
  icon1: "gloves",
  time2: "16:00",
  act2: "Kickboxing - Podsumowanie i tarcze",
  icon2: "gloves",
  desc: "Ostatnie szlify przed zakończeniem obozu. Lżejsze treningi mające na celu utrwalenie wiedzy, poprawę drobnych błędów i uniknięcie kontuzji przed powrotem do domu. Czas na indywidualne konsultacje z trenerami.",
  imageClassNameClassName: "absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent md:bg-gradient-to-r md:from-transparent md:to-card z-10 bg-cover bg-center !bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/41c81a8a-43ef-4a55-ba09-14423b66b6b9-e1774477964622.jpg)]"
}, {
  day: 12,
  title: "Wielki Finał",
  isMajor: true,
  image: "https://newwavecamp.com/wp-content/uploads/2026/03/1774475595355-7aca0500-5d9a-475f-96fb-7017779a4118-2-e1774479927617.png",
  imageClassName: "bg-cover bg-center bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/1774475595355-7aca0500-5d9a-475f-96fb-7017779a4118-2-e1774479927617.png)]",
  time1: "10:00",
  act1: "Rozdanie certyfikatów i podsumowanie",
  icon1: "sun",
  time2: "19:00",
  act2: "Pożegnalna impreza w tajskim stylu",
  icon2: "plate",
  desc: "Oficjalne zakończenie obozu. Uroczyste wręczenie certyfikatów uczestnictwa, wspólne zdjęcia i podsumowanie postępów. Wieczorem spotykamy się na wielkiej, pożegnalnej imprezie, by świętować przetrwanie 12 dni ciężkiej pracy!",
  imageClassNameClassName: "absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent md:bg-gradient-to-r md:from-transparent md:to-card z-10 bg-cover bg-center !bg-[url(https://newwavecamp.com/wp-content/uploads/2026/03/1774475595355-7aca0500-5d9a-475f-96fb-7017779a4118-2-e1774479927617.png)]"
}];
const getIcon = (type: string, className?: string) => {
  switch (type) {
    case "gloves":
      return <Dumbbell className={cn("text-primary", className)} />;
    case "sun":
      return <Sun className={cn("text-secondary", className)} />;
    case "plate":
      return <Utensils className={cn("text-chart-4", className)} />;
    default:
      return <SafeHydrate><Calendar className={cn("text-muted-foreground", className)} /></SafeHydrate>;
  }
};
export default function PlanBojowy12Dni() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: listRef,
    isVisible: isListVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="plan-bojowy-12-dni" className="dark bg-background text-foreground py-20 md:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        
        <div ref={headerRef} className={cn("text-center mb-16 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-sm font-bold tracking-widest text-primary uppercase mb-3">
            Harmonogram
          </h2>
          <h3 className="text-4xl md:text-5xl font-bold uppercase tracking-tight mb-6">
            Plan Bojowy: <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-chart-3">12 Dni</span>
          </h3>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Każdy dzień został starannie zaplanowany, aby zmaksymalizować Twój progres sportowy, 
            jednocześnie dając czas na regenerację i odkrywanie uroków Koh Samui.
          </p>
        </div>

        
        <div ref={listRef} className={cn("transition-all duration-1000 delay-200 ease-out", isListVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {planData.map((day, i) => <AccordionItem key={day.day} value={`day-${day.day}`} data-index={i} className={cn("bg-card border border-border/40 rounded-2xl overflow-hidden transition-all duration-300", "data-[state=open]:border-primary/50 data-[state=open]:shadow-lg data-[state=open]:shadow-primary/5", "hover:border-border/80")}>
                <AccordionTrigger className="px-0 py-0 hover:no-underline hover:bg-accent/5 transition-colors group [&>svg]:hidden">
                  <div className="flex flex-col md:flex-row w-full items-stretch text-left relative">
                    
                    
                    {day.image && <div className={cn("w-full h-48 md:h-auto shrink-0 relative overflow-hidden", day.day === 12 ? "md:w-[256px] md:-mt-2" : "md:w-64")}>
                        <div className={cn("absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent md:bg-gradient-to-r md:from-transparent md:to-card z-10", day.imageClassName)} />
                        <img src={day.image} alt={day.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        
                        <div className="absolute top-4 left-4 z-20 md:hidden bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full border border-border/50">
                          <span className="text-primary font-bold text-xs uppercase tracking-wider">
                            Dzień {day.day}
                          </span>
                        </div>
                      </div>}

                    
                    <div className={cn("flex-1 p-6 md:p-8 flex flex-col justify-center relative z-20", day.image ? "pt-4 md:pt-8" : "")}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={cn("text-primary font-bold text-sm uppercase tracking-wider", day.image ? "hidden md:block" : "block")}>
                          Dzień {day.day}
                        </div>
                        
                        <ChevronDown className="size-5 text-muted-foreground transition-transform duration-300 group-data-[state=open]:rotate-180" />
                      </div>
                      
                      <h4 className="text-2xl md:text-3xl font-bold uppercase text-card-foreground mb-6">
                        {day.title}
                      </h4>
                      
                      
                      <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 bg-background p-2 rounded-lg border border-border/50 shadow-sm">
                            {getIcon(day.icon1, "size-4")}
                          </div>
                          <div>
                            <div className="text-foreground font-bold text-sm">{day.time1}</div>
                            <div className="text-muted-foreground text-sm">{day.act1}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 bg-background p-2 rounded-lg border border-border/50 shadow-sm">
                            {getIcon(day.icon2, "size-4")}
                          </div>
                          <div>
                            <div className="text-foreground font-bold text-sm">{day.time2}</div>
                            <div className="text-muted-foreground text-sm">{day.act2}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                
                <AccordionContent className="px-6 md:px-8 pb-8 pt-2">
                  <div className={cn("border-t border-border/50 pt-6 mt-2", day.image ? "md:ml-64 md:pl-8 md:border-t-0 md:border-l md:pt-0 md:mt-0" : "")}>
                    <p className="text-muted-foreground leading-relaxed text-base">
                      {day.desc}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}